import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { includeBearerTokenInterceptor } from 'keycloak-angular';

// interceptors
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';

// primeNG
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';

// constants
import { InjectionSymbols } from '@sideline/sideline-apps-shared';

// config
import { app } from 'config/app.config';
import { analytics, provideFirebaseAnalytics, provideFirebaseApplication } from 'config/firebase.config';
import { provideKeycloakAngular } from 'config/keycloak.config';

// routes
import { routes } from './app.routes';

// services
import { AuthService, LoggingService, NavigationService } from '@sideline/sideline-apps-shared';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      }),
      // withEnabledBlockingInitialNavigation()
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Firebase
    provideFirebaseApplication,
    provideFirebaseAnalytics,
    ScreenTrackingService,
    UserTrackingService,
    // provideFirebaseAppCheck,

    // Keycloak
    provideKeycloakAngular(),
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, authErrorInterceptor])
    ),

    // PrimeNG
    providePrimeNG({
      theme: {
        preset: Lara,
        options: { darkModeSelector: '.app-dark' }
      }
    }),

    // services
    AuthService,
    LoggingService,
    NavigationService,

    // Tokens
    { provide: InjectionSymbols.CAPTCHA_KEY, useValue: app.captchaKey },
    { provide: InjectionSymbols.CLIENT_ID, useValue: app.clientId},
    { provide: InjectionSymbols.CLIENT_REALM, useValue: app.clientRealm},
    { provide: InjectionSymbols.CONFIG_MAINTENANCE_MODE, useValue: app.config.maintenanceMode },
    { provide: InjectionSymbols.FIREBASE_ANALYTICS, useValue: analytics},
    { provide: InjectionSymbols.LOG_LEVEL, useValue: app.config.logging.level },
    { provide: InjectionSymbols.LOG_TO_CONSOLE, useValue: app.config.logging.console },
    { provide: InjectionSymbols.LOG_TO_BROWSER_STORAGE, useValue: app.config.logging.browserStorage },
    { provide: InjectionSymbols.LOG_TO_EXTERNAL, useValue: app.config.logging.external },
    { provide: InjectionSymbols.NAME_BRAND, useValue: app.marketing.names.brand },
    { provide: InjectionSymbols.NAME_SITE, useValue: app.marketing.names.product },
    { provide: InjectionSymbols.ROUTE_DEFAULT_PRIVATE, useValue: app.routes.defaultPrivate },
    { provide: InjectionSymbols.ROUTE_DEFAULT_PUBLIC, useValue: app.routes.defaultPublic },
    { provide: InjectionSymbols.ROUTE_ROOT, useValue: app.routes.root },
    { provide: InjectionSymbols.URL_AUTH, useValue: app.urls.auth},
    { provide: InjectionSymbols.URL_CONTACT_MANAGER, useValue: app.urls.contactManager },
    { provide: InjectionSymbols.URL_EVENT_MANAGER, useValue: app.urls.eventManager},
    // { provide: InjectionSymbols.URL_NOTIFICATIONS, useValue: app.urls.notifications },
    { provide: InjectionSymbols.URL_RESERVATION_MANAGER, useValue: app.urls.reservationManager},
    { provide: InjectionSymbols.URL_SEND, useValue: app.urls.send },
    { provide: InjectionSymbols.URL_STRINGER, useValue: app.urls.stringer },
  ]
};
